import { ArrowDownRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { FeaturedReel } from "components/FeaturedReel";
import { mediaUrl } from "lib/media";

const featuredWork = [
  {
    title: "Planetary Explosion",
    discipline: "Shading / Compositing",
    video: mediaUrl("website_files/videos/planet/PlanetBreak2.mov"),
    previewTime: 1.5,
    href: "/demo-reel?video=planetary%20explosion",
    className: "md:col-span-7 md:row-span-2",
  },
  {
    title: "Metal Disintegration",
    discipline: "Destruction / Houdini",
    video: mediaUrl("website_files/videos/sword2/Disintegrate2.mov"),
    previewTime: 1.5,
    href: "/demo-reel?video=disintegrate",
    className: "md:col-span-5",
  },
  {
    title: "Ocean",
    discipline: "Ocean Simulation / Houdini",
    video: mediaUrl("website_files/videos/ship/ship2.mov"),
    previewTime: 2,
    href: "/demo-reel?video=ocean",
    className: "md:col-span-5",
  },
  {
    title: "Tornado",
    discipline: "Atmospheric FX / Houdini",
    video: mediaUrl("website_files/videos/tornado/tornado_breakdown.mov"),
    previewTime: 1.5,
    href: "/demo-reel?video=tornado",
    className: "md:col-span-5",
  },
  {
    title: "Underwater",
    discipline: "Compositing / Nuke",
    video: mediaUrl("website_files/videos/underwater/underwater.mov"),
    previewTime: 2,
    href: "/demo-reel?video=underwater",
    className: "md:col-span-7",
  },
];

export const Home = () => {
  return (
    <div className="overflow-hidden bg-background text-foreground">
      <section className="relative isolate border-b border-white/10">
        <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_75%_25%,rgba(255,92,54,0.12),transparent_30%)]" />
        <div className="page-shell grid min-h-[calc(100svh-82px)] items-center gap-12 py-16 lg:grid-cols-12 lg:gap-8 lg:py-24">
          <div className="relative z-10 lg:col-span-5">
            <p className="eyebrow reveal-up">
              Los Angeles · FX artist &amp; compositor
            </p>
            <h1 className="hero-title reveal-up reveal-delay-1 mt-7 max-w-4xl">
              Building worlds
              <span className="block text-ember">in motion.</span>
            </h1>
            <p className="reveal-up reveal-delay-2 mt-8 max-w-xl text-base leading-8 text-white/60 sm:text-lg">
              I’m Gregory Kalamdaryan, a visual effects artist creating
              cinematic simulations, procedural systems, and polished composites
              with Houdini and Nuke.
            </p>
            <div className="reveal-up reveal-delay-3 mt-10 flex flex-wrap gap-3">
              <Link className="button-primary group" to="/demo-reel">
                View selected work
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link className="button-secondary" to="/about">
                About Gregory
              </Link>
            </div>
          </div>

          <div className="reveal-up reveal-delay-2 relative lg:col-span-7 lg:pl-8">
            <div className="absolute -left-10 -top-10 hidden h-28 w-px bg-gradient-to-b from-transparent via-ember to-transparent lg:block" />
            <FeaturedReel />
            <div className="mt-5 flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-white/40">
              <span>Gregory Kalamdaryan · Demo Reel</span>
              <span className="hidden sm:inline">00:40 · 1080p</span>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="page-shell grid divide-y divide-white/10 py-2 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          <Stat value="7+" label="Years in visual effects" />
          <Stat value="Houdini" label="Procedural FX & simulation" />
          <Stat value="Nuke" label="Compositing & finishing" />
        </div>
      </section>

      <section className="page-shell py-24 sm:py-32">
        <div className="mb-12 flex flex-col justify-between gap-7 sm:flex-row sm:items-end sm:gap-10">
          <div>
            <p className="eyebrow">Selected work</p>
            <h2 className="section-title mt-5">Frames from the process.</h2>
          </div>
          <Link
            to="/demo-reel"
            className="text-link group shrink-0 self-start sm:self-auto"
          >
            Explore all projects
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <div className="grid auto-rows-[270px] gap-4 md:grid-cols-12 md:auto-rows-[210px] lg:auto-rows-[260px]">
          {featuredWork.map((work) => (
            <Link
              to={work.href}
              key={work.title}
              className={`project-card group relative overflow-hidden bg-white/5 ${work.className}`}
            >
              <video
                src={`${work.video}#t=${work.previewTime}`}
                className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105"
                muted
                playsInline
                preload="metadata"
                aria-hidden="true"
                tabIndex={-1}
                onLoadedMetadata={(event) => {
                  event.currentTarget.currentTime = work.previewTime;
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent transition group-hover:from-black" />
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 sm:p-7">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
                    {work.discipline}
                  </p>
                  <h3 className="mt-2 text-xl font-semibold sm:text-2xl">
                    {work.title}
                  </h3>
                </div>
                <span className="grid h-11 w-11 shrink-0 translate-y-2 place-items-center rounded-full border border-white/25 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:border-ember group-hover:bg-ember group-hover:opacity-100">
                  <ArrowDownRight className="h-5 w-5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.025]">
        <div className="page-shell grid gap-12 py-24 lg:grid-cols-12 lg:gap-8 lg:py-32">
          <div className="lg:col-span-5">
            <p className="eyebrow">Approach</p>
            <h2 className="section-title mt-5 max-w-lg">
              From first sim to final pixel.
            </h2>
          </div>
          <div className="grid gap-10 sm:grid-cols-2 lg:col-span-7">
            <Capability
              number="01"
              title="Procedural FX"
              description="Flexible Houdini systems for fluids, destruction, atmospherics, growth, and transformation."
            />
            <Capability
              number="02"
              title="Look development"
              description="Lighting and material choices that keep the effect grounded in the scene and serve the story."
            />
            <Capability
              number="03"
              title="Compositing"
              description="Nuke workflows that integrate CG with live action through color, depth, atmosphere, and detail."
            />
            <Capability
              number="04"
              title="Problem solving"
              description="Iterative, production-minded solutions built to balance visual quality with render efficiency."
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const Stat = ({ value, label }: { value: string; label: string }) => (
  <div className="flex items-baseline gap-4 py-7 sm:block sm:px-8 sm:py-8 first:sm:pl-0">
    <p className="text-2xl font-semibold tracking-[-0.03em] text-white sm:text-3xl">
      {value}
    </p>
    <p className="mt-2 text-xs uppercase tracking-[0.16em] text-white/40">
      {label}
    </p>
  </div>
);

const Capability = ({
  number,
  title,
  description,
}: {
  number: string;
  title: string;
  description: string;
}) => (
  <div className="border-t border-white/10 pt-5">
    <p className="text-xs font-semibold text-ember">{number}</p>
    <h3 className="mt-6 text-xl font-semibold">{title}</h3>
    <p className="mt-3 text-sm leading-7 text-white/50">{description}</p>
  </div>
);
